<script>
import { mapActions, mapGetters } from "vuex";

import sizeVue from "@/mixins/size.vue";
import RegionAdd from "../../components/modals/RegionAdd.vue";

export default {
  components: { RegionAdd },
  data: () => ({
    isLoading: false,

    options: {},
    totalItemsCount: 0,
    itemsPerPage: 50,
    rowsPerPage: [25, 50, 100],

    search: null,

    isAddModal: false,

    selected: {},
  }),

  mixins: [sizeVue],

  created() {
    this.INIT_COUNTRIES_ALL();
  },

  computed: {
    tableHeight: (vm) => vm.size.height - 280,

    ...mapGetters("admin-regions", ["GET_REGIONS"]),
    items: (vm) => vm.GET_REGIONS,

    ...mapGetters("admin-countries", ["GET_COUNTRIES_ALL"]),
    countries: (vm) => vm.GET_COUNTRIES_ALL,
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("admin-regions", ["INIT_REGIONS", "REGION_STATUS"]),
    ...mapActions("admin-countries", ["INIT_COUNTRIES_ALL"]),

    getItems() {
      if (this.isLoading) return;
      this.isLoading = true;

      this.options.search = this.search;

      this.INIT_REGIONS(this.options).then((totalItemsCount) => {
        this.totalItemsCount = totalItemsCount;
        this.isLoading = false;
      });
    },

    onAddModel() {
      this.selected = {};

      this.$nextTick(() => {
        this.isAddModal = true;
      });
    },

    onEdit({ item = {} }) {
      this.selected = item;

      this.$nextTick(() => {
        this.isAddModal = true;
      });
    },

    onSwitch({ item = {} }) {
      if (this.isLoading) return;

      this.isLoading = false;
      this.REGION_STATUS(item).then(() => {
        this.isLoading = false;
        this.$nextTick(() => {
          this.getItems();
        });
      });
    },

    getName: (item) => item?.country?.name,
  },
};
</script>

<template>
  <v-container fluid>
    <region-add
      v-if="isAddModal"
      @close="isAddModal = false"
      @refresh="getItems"
      :data="selected"
    />

    <v-card class="pa-5">
      <div class="toolbar-row pb-5">
        <v-text-field
          :placeholder="$t('Izlash...')"
          append-icon="mdi-magnify"
          v-model="search"
          dense
          hide-details
          outlined
          clearable
          class="search-input"
          @click:append="getItems"
          @keyup.enter="getItems"
          @click:clear="getItems"
        />
        <div class="circle-actions">
          <v-btn elevation="0" class="toolbar-btn" @click="onAddModel">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn elevation="0" class="toolbar-btn" :loading="isLoading" @click="getItems">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :mobile-breakpoint="0"
        :height="tableHeight"
        :loading="isLoading"
        :headers="[
          {
            text: '№',
            value: 'row-number',
            width: 20,
            sortable: false,
          },
          {
            text: $t('Country'),
            value: 'country',
          },
          {
            text: $t('Name uz'),
            value: 'name->uz',
          },
          {
            text: $t('Name ru'),
            value: 'name->ru',
          },
          {
            text: $t('Name en'),
            value: 'name->en',
          },
          {
            text: '',
            value: 'edit',
            width: 20,
            sortable: false,
          },
          {
            text: '',
            value: 'block',
            width: 20,
            sortable: false,
          },
        ]"
        fixed-header
        :items="items"
        :options.sync="options"
        :server-items-length="totalItemsCount"
        item-key="id"
        :items-per-page="itemsPerPage"
        :footer-props="{ itemsPerPageOptions: rowsPerPage }"
        class="elevation-0"
      >
        <template v-slot:body="props">
          <tbody>
            <tr :key="item.id" v-for="(item, index) in props.items">
              <td v-text="index + 1" />
              <td v-text="getName(item)" />
              <td v-text="item.names.uz" />
              <td v-text="item.names.ru" />
              <td v-text="item.names.en" />
              <td class="px-2">
                <button class="tc-act" :title="$t('Edit')" @click="onEdit({ item })">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                </button>
              </td>
              <td @click="onSwitch({ item })" class="pr-0">
                <v-switch
                  :loading="isLoading"
                  hide-details
                  class="mt-0"
                  v-model="item.active"
                  inset
                />
              </td>
            </tr>
            <tr v-if="!props.items.length && !isLoading">
              <td :colspan="props.headers.length + 1" class="text-center py-4">
                {{ $t("No data") }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style lang="scss" scoped>
/* ── Toolbar Row ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-row > .search-input {
  flex: 1;
  min-width: 0;
}

.circle-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.toolbar-btn {
  min-width: 38px !important;
  width: 38px !important;
  height: 38px !important;
  border-radius: 10px !important;
  border: 1px solid #e0e3eb !important;
  background: #fff !important;
  padding: 0 !important;
}

.toolbar-btn:hover {
  border-color: #d0d5dd !important;
  background: #f9fafb !important;
}

.theme--dark .toolbar-btn {
  background: #22232e !important;
  border-color: #33354a !important;
}

.theme--dark .toolbar-btn:hover {
  background: #2a2c3a !important;
  border-color: #3a3c4e !important;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}
/* Row action buttons */
.tc-actions { display: flex; gap: 6px; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

.theme--dark .tc-act { border-color: #2a2c3a; color: #9ca3af; }
.theme--dark .tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.theme--dark .tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }
</style>
